import { useCallback, useEffect, useState } from 'react';
import { Bot, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';

type CompanyOpt = { id: string; name: string };

export function RiqueAtendimentoPromptTab() {
  const [companies, setCompanies] = useState<CompanyOpt[]>([]);
  const [companyId, setCompanyId] = useState<string>('');
  const [enabled, setEnabled] = useState(true);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const res = await api.get('/companies');
        const list = (res.data?.data ?? res.data ?? []) as CompanyOpt[];
        setCompanies(Array.isArray(list) ? list : []);
      } catch {
        setCompanies([]);
      }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/rique-atendimento/config', {
        params: companyId ? { companyId } : {},
      });
      const data = res.data?.data ?? {};
      setEnabled(data.enabled !== false);
      setSystemPrompt(data.systemPrompt || '');
      setWelcomeMessage(data.welcomeMessage || '');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Falha ao carregar prompt');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    if (systemPrompt.trim().length < 20) {
      toast.error('O system prompt deve ter pelo menos 20 caracteres.');
      return;
    }
    setSaving(true);
    try {
      await api.put('/rique-atendimento/config', {
        companyId: companyId || null,
        enabled,
        systemPrompt: systemPrompt.trim(),
        welcomeMessage: welcomeMessage.trim() || null,
      });
      toast.success('Prompt de atendimento atualizado.');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Falha ao salvar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bot className="w-5 h-5" />
          Prompt — Rique.Ia Atendimento
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Define o comportamento da IA no chat de abertura de demandas. Sem empresa selecionada, edita o
          fallback global.
        </p>

        <div className="space-y-2">
          <label className="text-sm font-medium">Empresa</label>
          <Select value={companyId} onValueChange={setCompanyId} disabled={loading}>
            <option value="">Global (fallback)</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            disabled={loading}
          />
          Atendimento habilitado
        </label>

        <div className="space-y-2">
          <label className="text-sm font-medium">Mensagem de boas-vindas</label>
          <Input
            value={welcomeMessage}
            onChange={(e) => setWelcomeMessage(e.target.value)}
            disabled={loading}
            placeholder="Olá! Sou o Rique.Ia…"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">System prompt</label>
          <Textarea
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
            disabled={loading}
            rows={14}
            className="font-mono text-sm"
          />
        </div>

        <Button onClick={() => void save()} disabled={saving || loading}>
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Salvando…' : 'Salvar prompt'}
        </Button>
      </CardContent>
    </Card>
  );
}
